import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Alert,
  CircularProgress,
  Stack,
  Avatar,
  Badge,
  Tooltip,
  Snackbar,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  Divider,
} from '@mui/material';
import {
  Search,
  FilterList,
  Refresh,
  Visibility,
  Edit,
  Delete,
  Add,
  SportsTennis,
  Pool,
  FitnessCenter,
  Spa,
  BusinessCenter,
  LocalParking,
  Restaurant,
  MeetingRoom,
  Wifi,
  RoomService,
  Category,
  Receipt,
  Print,
  Download,
  Today,
  CalendarMonth,
  Discount,
  Person,
  VerifiedUser,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../../../../components/sidebars/sidebar_reception';
import TopNav from '../../../../components/TopNav_hotelStaff';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
// At the top of your file, after the imports, add these constants:
const HOTEL_NAME = process.env.REACT_APP_HOTEL_NAME || 'e-Neurons Luxury Hotel';
const HOTEL_ADDRESS = process.env.REACT_APP_HOTEL_ADDRESS || 'Luxury Hotel Address';
const HOTEL_PHONE = process.env.REACT_APP_HOTEL_PHONE;

const FacilitySalesManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [guests, setGuests] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState(0);
  const [currentStaff, setCurrentStaff] = useState(null);
  const [receiptData, setReceiptData] = useState(null);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  
  // Filter states
  const [filters, setFilters] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    facilityId: '',
    bookingStatus: '',
    paymentStatus: '',
    createdBy: '',
    dateRange: 'today',
  });
  
  // Booking form states
  const [bookingForm, setBookingForm] = useState({
    facility_id: '',
    guest_id: '',
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    booking_date: new Date().toISOString().split('T')[0],
    booking_time: '10:00',
    duration: 60,
    number_of_guests: 1,
    special_requests: '',
    payment_method: 'Cash',
  });
  
  // Dialog states
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Navigation and sidebar
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Snackbar function
  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userProfile');
    navigate('/Entri');
  };

  const handleNotificationClick = () => {
    showSnackbar('You have new notifications!', 'info');
  };

  // Get hotel code and staff ID
  const getHotelCode = () => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      return storedProfile.hotelcode || storedProfile.hotel_code || storedProfile.hotelCode || 'eHT5678042024';
    } catch (error) {
      console.error('Error getting hotel code:', error);
      return 'eHT5678042024';
    }
  };

  const getStaffId = () => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      return storedProfile.staff_id || storedProfile.id || null;
    } catch (error) {
      console.error('Error getting staff ID:', error);
      return null;
    }
  };

  // Get current staff details
  const getCurrentStaff = () => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      setCurrentStaff(storedProfile);
      return storedProfile;
    } catch (error) {
      console.error('Error getting staff details:', error);
      return null;
    }
  };

  // API service functions
  const api = {
    // Get all facilities
    getFacilities: async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/hotel/facilities/facilities.php`, {
          params: { hotel_code: getHotelCode() }
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching facilities:', error);
        return { success: false, facilities: [] };
      }
    },

    // Get facility bookings
    getBookings: async (params = {}) => {
      try {
        const response = await axios.get(`${API_BASE_URL}/hotel/facilities/facility-bookings.php`, {
          params: { hotel_code: getHotelCode(), ...params }
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching bookings:', error);
        return { success: false, bookings: [] };
      }
    },

    // Get single booking details
    getBookingDetails: async (bookingId) => {
      try {
        const response = await axios.get(`${API_BASE_URL}/hotel/facilities/facility-bookings.php`, {
          params: { 
            hotel_code: getHotelCode(),
            id: bookingId 
          }
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching booking details:', error);
        return { success: false };
      }
    },

    // Create facility booking
    createBooking: async (bookingData) => {
      try {
        const response = await axios.post(`${API_BASE_URL}/hotel/facilities/facility-bookings.php`, {
          ...bookingData,
          hotel_code: getHotelCode(),
          created_by: getStaffId(),
          payment_status: 'paid',
          paid_amount: bookingData.total_amount
        });
        return response.data;
      } catch (error) {
        console.error('Error creating booking:', error);
        return { success: false, error: 'Failed to create booking' };
      }
    },

    // Update booking status
    updateBookingStatus: async (id, status, reason = null) => {
      try {
        const response = await axios.put(`${API_BASE_URL}/hotel/facilities/facility-bookings.php`, {
          action: 'update_status',
          id: id,
          hotel_code: getHotelCode(),
          booking_status: status,
          staff_id: getStaffId(),
          cancellation_reason: reason
        });
        return response.data;
      } catch (error) {
        console.error('Error updating booking status:', error);
        return { success: false, error: 'Failed to update booking status' };
      }
    },

    // Get statistics
    getStatistics: async (params = {}) => {
      try {
        const response = await axios.get(`${API_BASE_URL}/hotel/facilities/facility-bookings.php`, {
          params: { 
            hotel_code: getHotelCode(), 
            action: 'statistics',
            ...params 
          }
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching statistics:', error);
        return { success: false, statistics: {} };
      }
    },

    // Get staff list
    getStaff: async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/hotel/facilities/staff.php`, {
          params: { hotel_code: getHotelCode() }
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching staff:', error);
        return { success: false, staff: [] };
      }
    },

    // Search guests
    searchGuests: async (searchTerm) => {
      try {
        const response = await axios.get(`${API_BASE_URL}/hotel/facilities/guests.php`, {
          params: { 
            hotel_code: getHotelCode(),
            search: searchTerm,
            limit: 10
          }
        });
        return response.data;
      } catch (error) {
        console.error('Error searching guests:', error);
        return { success: false, guests: [] };
      }
    }
  };

  // Load initial data
  useEffect(() => {
    getCurrentStaff();
    loadData();
    loadStaff();
  }, []);

  // Apply filters when they change
  useEffect(() => {
    applyFilters();
  }, [filters, bookings]);

  // Handle date range changes
  useEffect(() => {
    handleDateRangeChange(filters.dateRange);
  }, [filters.dateRange]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [facilitiesData, bookingsData, statsData] = await Promise.all([
        api.getFacilities(),
        api.getBookings(),
        api.getStatistics({
          start_date: filters.startDate,
          end_date: filters.endDate
        })
      ]);

      if (facilitiesData.success) {
        setFacilities(facilitiesData.facilities || []);
      }

      if (bookingsData.success) {
        setBookings(bookingsData.bookings || []);
      }

      if (statsData.success) {
        setStatistics(statsData.statistics || {});
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStaff = async () => {
    try {
      const result = await api.getStaff();
      if (result.success) {
        setStaffList(result.staff || []);
      }
    } catch (error) {
      console.error('Error loading staff:', error);
    }
  };

  // Handle date range presets
  const handleDateRangeChange = (range) => {
    const today = new Date();
    
    switch (range) {
      case 'today':
        setFilters(prev => ({
          ...prev,
          startDate: today.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0],
          dateRange: 'today'
        }));
        break;
        
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        setFilters(prev => ({
          ...prev,
          startDate: yesterday.toISOString().split('T')[0],
          endDate: yesterday.toISOString().split('T')[0],
          dateRange: 'yesterday'
        }));
        break;
        
      case 'thisWeek':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        setFilters(prev => ({
          ...prev,
          startDate: startOfWeek.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0],
          dateRange: 'thisWeek'
        }));
        break;
        
      case 'thisMonth':
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        setFilters(prev => ({
          ...prev,
          startDate: startOfMonth.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0],
          dateRange: 'thisMonth'
        }));
        break;
        
      case 'custom':
        setFilters(prev => ({
          ...prev,
          dateRange: 'custom'
        }));
        break;
        
      default:
        break;
    }
  };

  const applyFilters = () => {
    let filtered = [...bookings];

    if (filters.startDate) {
      filtered = filtered.filter(booking => 
        booking.booking_date >= filters.startDate
      );
    }

    if (filters.endDate) {
      filtered = filtered.filter(booking => 
        booking.booking_date <= filters.endDate
      );
    }

    if (filters.facilityId) {
      filtered = filtered.filter(booking => 
        booking.facility_id == filters.facilityId
      );
    }

    if (filters.bookingStatus) {
      filtered = filtered.filter(booking => 
        booking.booking_status === filters.bookingStatus
      );
    }

    if (filters.paymentStatus) {
      filtered = filtered.filter(booking => 
        booking.payment_status === filters.paymentStatus
      );
    }

    if (filters.createdBy) {
      filtered = filtered.filter(booking => 
        booking.created_by == filters.createdBy
      );
    }

    setFilteredBookings(filtered);
    setPage(0);
  };

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleDateChange = (field) => (event) => {
    setFilters(prev => ({
      ...prev,
      [field]: event.target.value,
      dateRange: 'custom'
    }));
  };

  const clearFilters = () => {
    const today = new Date().toISOString().split('T')[0];
    setFilters({
      startDate: today,
      endDate: today,
      facilityId: '',
      bookingStatus: '',
      paymentStatus: '',
      createdBy: '',
      dateRange: 'today',
    });
  };

  // Get facility with active discount
  const getFacilityWithDiscount = (facility) => {
    if (!facility) return { facility, discountPercentage: 0, discountReason: '' };
    
    const today = new Date().toISOString().split('T')[0];
    let discountPercentage = parseFloat(facility.discount_percentage) || 0;
    let discountReason = '';
    
    // Check if discount is active based on dates
    const isValidFrom = !facility.discount_valid_from || facility.discount_valid_from === '0000-00-00' || facility.discount_valid_from <= today;
    const isValidTo = !facility.discount_valid_to || facility.discount_valid_to === '0000-00-00' || facility.discount_valid_to >= today;
    
    if (discountPercentage > 0 && isValidFrom && isValidTo) {
      discountReason = 'Facility Special Discount';
    } else {
      discountPercentage = 0;
    }
    
    return { facility, discountPercentage, discountReason };
  };

  // Calculate total amount with automatic discount from database
  const calculateTotalAmount = () => {
    const facility = facilities.find(f => f.id == bookingForm.facility_id);
    if (!facility) return 0;
    
    let baseAmount = parseFloat(facility.booking_fee || 0);
    
    // Additional guest charges
    if (bookingForm.number_of_guests > 1) {
      baseAmount += (bookingForm.number_of_guests - 1) * 1000;
    }
    
    // Get active discount from database
    const { discountPercentage } = getFacilityWithDiscount(facility);
    const discountAmount = baseAmount * (discountPercentage / 100);
    const finalAmount = baseAmount - discountAmount;
    
    return Math.round(finalAmount);
  };

  // Calculate discount amount
  const calculateDiscountAmount = () => {
    const facility = facilities.find(f => f.id == bookingForm.facility_id);
    if (!facility) return 0;
    
    let baseAmount = parseFloat(facility.booking_fee || 0);
    
    if (bookingForm.number_of_guests > 1) {
      baseAmount += (bookingForm.number_of_guests - 1) * 1000;
    }
    
    const { discountPercentage } = getFacilityWithDiscount(facility);
    return baseAmount * (discountPercentage / 100);
  };

  // Get current discount percentage
  const getCurrentDiscountPercentage = () => {
    const facility = facilities.find(f => f.id == bookingForm.facility_id);
    if (!facility) return 0;
    const { discountPercentage } = getFacilityWithDiscount(facility);
    return discountPercentage;
  };

  // Prepare receipt data from existing booking
  const prepareReceiptFromBooking = async (booking) => {
    setLoading(true);
    try {
      // Find the facility details
      const facility = facilities.find(f => f.id == booking.facility_id);
      
      // Calculate discount if any
      const { discountPercentage } = getFacilityWithDiscount(facility);
      const baseAmount = parseFloat(booking.total_amount) / (1 - (discountPercentage / 100));
      const discountAmount = baseAmount * (discountPercentage / 100);
      
      // Prepare receipt data structure
      const receipt = {
        booking: {
          id: booking.id,
          booking_number: booking.booking_number,
        },
        facility: facility || {
          name: booking.facility_name,
          booking_fee: booking.total_amount
        },
        guest: {
          guest_name: booking.guest_name || 'Walk-in Guest',
          guest_email: booking.guest_email || '',
          guest_phone: booking.guest_phone || ''
        },
        staff: currentStaff,
        totalAmount: parseFloat(booking.total_amount),
        discountPercentage: discountPercentage,
        discountAmount: discountAmount,
        baseAmount: baseAmount,
        booking_date: booking.booking_date,
        booking_time: booking.booking_time,
        duration: booking.duration,
        number_of_guests: booking.number_of_guests,
        payment_method: booking.payment_method || 'Cash',
        special_requests: booking.special_requests || '',
        date: new Date(),
        hotelCode: getHotelCode()
      };
      
      setReceiptData(receipt);
      setPrintDialogOpen(true);
    } catch (error) {
      console.error('Error preparing receipt:', error);
      showSnackbar('Failed to load receipt data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBookingInputChange = (field) => (event) => {
    const value = event.target.value;
    setBookingForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateBookingForm = () => {
    const errors = {};
    
    if (!bookingForm.facility_id) {
      errors.facility_id = 'Please select a facility';
    }
    
    if (!bookingForm.guest_name || bookingForm.guest_name.trim() === '') {
      errors.guest_name = 'Guest name is required';
    }
    
    if (!bookingForm.booking_date) {
      errors.booking_date = 'Booking date is required';
    }
    
    if (!bookingForm.booking_time) {
      errors.booking_time = 'Booking time is required';
    }
    
    if (!bookingForm.payment_method) {
      errors.payment_method = 'Payment method is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNewBooking = () => {
    setBookingForm({
      facility_id: '',
      guest_id: '',
      guest_name: '',
      guest_email: '',
      guest_phone: '',
      booking_date: new Date().toISOString().split('T')[0],
      booking_time: '10:00',
      duration: 60,
      number_of_guests: 1,
      special_requests: '',
      payment_method: 'Cash',
    });
    setFormErrors({});
    setActiveStep(0);
    setBookingDialogOpen(true);
  };

  const handleSearchGuests = async (searchTerm) => {
    if (searchTerm.length < 3) return;
    
    try {
      const result = await api.searchGuests(searchTerm);
      if (result.success) {
        setGuests(result.guests || []);
      }
    } catch (error) {
      console.error('Error searching guests:', error);
    }
  };

  const handleSelectGuest = (guest) => {
    setBookingForm(prev => ({
      ...prev,
      guest_id: guest.id,
      guest_name: `${guest.first_name} ${guest.last_name}`,
      guest_email: guest.email,
      guest_phone: guest.phone,
    }));
  };

  const handleNextStep = () => {
    if (activeStep === 0 && !bookingForm.facility_id) {
      showSnackbar('Please select a facility', 'error');
      return;
    }
    
    if (activeStep === 1 && (!bookingForm.guest_name || bookingForm.guest_name.trim() === '')) {
      showSnackbar('Please enter guest name', 'error');
      return;
    }
    
    if (activeStep === 2 && (!bookingForm.booking_date || !bookingForm.booking_time)) {
      showSnackbar('Please select booking date and time', 'error');
      return;
    }
    
    setActiveStep((prev) => prev + 1);
  };

  const handleBackStep = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmitBooking = async () => {
    if (!validateBookingForm()) {
      const firstError = Object.values(formErrors)[0];
      showSnackbar(firstError, 'error');
      return;
    }
    
    try {
      const selectedFacility = facilities.find(f => f.id == bookingForm.facility_id);
      if (!selectedFacility) {
        showSnackbar('Please select a facility', 'error');
        return;
      }

      const totalAmount = calculateTotalAmount();
      const discountPercentage = getCurrentDiscountPercentage();
      const discountAmount = calculateDiscountAmount();
      
      const bookingData = {
        ...bookingForm,
        total_amount: totalAmount,
        paid_amount: totalAmount,
        payment_status: 'paid',
        discount_percentage: discountPercentage,
        discount_reason: discountPercentage > 0 ? 'Facility Special Discount' : '',
        discount_amount: discountAmount
      };

      const result = await api.createBooking(bookingData);
      if (result.success) {
        // Prepare receipt data
        setReceiptData({
          booking: result,
          facility: selectedFacility,
          guest: bookingForm,
          staff: currentStaff,
          totalAmount: totalAmount,
          discountPercentage: discountPercentage,
          discountAmount: discountAmount,
          baseAmount: parseFloat(selectedFacility.booking_fee || 0) + (bookingForm.number_of_guests > 1 ? (bookingForm.number_of_guests - 1) * 1000 : 0),
          booking_date: bookingForm.booking_date,
          booking_time: bookingForm.booking_time,
          duration: bookingForm.duration,
          number_of_guests: bookingForm.number_of_guests,
          payment_method: bookingForm.payment_method,
          special_requests: bookingForm.special_requests,
          date: new Date(),
          hotelCode: getHotelCode()
        });
        
        showSnackbar('Booking created successfully!', 'success');
        setBookingDialogOpen(false);
        setPrintDialogOpen(true);
        loadData();
      } else {
        showSnackbar(result.error || 'Failed to create booking', 'error');
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      showSnackbar('Failed to create booking', 'error');
    }
  };

  const handlePrintReceipt = () => {
    const printContent = document.getElementById('receipt-content').innerHTML;
    const originalContents = document.body.innerHTML;
    
    document.body.innerHTML = printContent;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  const handleViewDetails = (booking) => {
    setSelectedBooking(booking);
    setDetailsDialogOpen(true);
  };

  // Calculate statistics
  const calculateStats = () => {
    const totalRevenue = filteredBookings.reduce((sum, booking) => sum + parseFloat(booking.total_amount || 0), 0);
    const totalCollected = filteredBookings.reduce((sum, booking) => sum + parseFloat(booking.paid_amount || 0), 0);
    
    return {
      totalRevenue,
      totalCollected,
      totalBookings: filteredBookings.length,
      pendingPayments: filteredBookings.filter(b => b.payment_status === 'pending').length,
      ...statistics
    };
  };

  const stats = calculateStats();

  // Status chip colors
  const getStatusColor = (status) => {
    const statusColors = {
      confirmed: 'primary',
      checked_in: 'info',
      completed: 'success',
      cancelled: 'error',
      paid: 'success',
      pending: 'warning',
      partial: 'info',
    };
    return statusColors[status] || 'default';
  };

  // Get date range display text
  const getDateRangeText = () => {
    switch (filters.dateRange) {
      case 'today':
        return "Today's Facility Bookings";
      case 'yesterday':
        return "Yesterday's Facility Bookings";
      case 'thisWeek':
        return "This Week's Facility Bookings";
      case 'thisMonth':
        return "This Month's Facility Bookings";
      case 'custom':
        return `Facility Bookings from ${filters.startDate} to ${filters.endDate}`;
      default:
        return "Today's Facility Bookings";
    }
  };

  const bookingSteps = ['Select Facility', 'Customer Details', 'Booking Details', 'Payment'];

  // Time slots for booking
  const timeSlots = [
    '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', 
    '16:00', '17:00', '18:00', '19:00', '20:00'
  ];

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          maxWidth: '1400px',
          mx: 'auto',
        }}
      >
        <TopNav
          onLogout={handleLogout}
          onNotificationClick={handleNotificationClick}
          toggleSidebar={toggleSidebar}
        />

        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Paper elevation={2} sx={{ p: 3, mb: 3, background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`, color: 'white' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  Facility Sales & Bookings
                </Typography>
                <Typography variant="subtitle1">
                  {getDateRangeText()}
                </Typography>
                {currentStaff && (
                  <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                    <Person sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                    Staff: {currentStaff.first_name} {currentStaff.last_name || ''} ({currentStaff.user_type})
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleNewBooking}
                  sx={{ backgroundColor: 'white', color: 'primary.main', '&:hover': { backgroundColor: 'grey.100' } }}
                >
                  New Booking
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={() => setFilterDialogOpen(true)}
                  sx={{ borderColor: 'white', color: 'white', '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' } }}
                >
                  Filters
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={loadData}
                  disabled={loading}
                  sx={{ borderColor: 'white', color: 'white', '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' } }}
                >
                  Refresh
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Date Range Quick Filters */}
          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Date Filters
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              <Button
                variant={filters.dateRange === 'today' ? 'contained' : 'outlined'}
                startIcon={<Today />}
                onClick={() => handleDateRangeChange('today')}
                size="small"
              >
                Today
              </Button>
              <Button
                variant={filters.dateRange === 'yesterday' ? 'contained' : 'outlined'}
                startIcon={<CalendarMonth />}
                onClick={() => handleDateRangeChange('yesterday')}
                size="small"
              >
                Yesterday
              </Button>
              <Button
                variant={filters.dateRange === 'thisWeek' ? 'contained' : 'outlined'}
                startIcon={<CalendarMonth />}
                onClick={() => handleDateRangeChange('thisWeek')}
                size="small"
              >
                This Week
              </Button>
              <Button
                variant={filters.dateRange === 'thisMonth' ? 'contained' : 'outlined'}
                startIcon={<CalendarMonth />}
                onClick={() => handleDateRangeChange('thisMonth')}
                size="small"
              >
                This Month
              </Button>
              <Button
                variant={filters.dateRange === 'custom' ? 'contained' : 'outlined'}
                startIcon={<CalendarMonth />}
                onClick={() => handleDateRangeChange('custom')}
                size="small"
              >
                Custom Range
              </Button>
            </Stack>
          </Paper>

          {/* Statistics Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Total Revenue
                      </Typography>
                      <Typography variant="h4" color="primary">
                        ₦{stats.totalRevenue.toLocaleString()}
                      </Typography>
                    </Box>
                    <Receipt sx={{ fontSize: 40, color: 'primary.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Amount Collected
                      </Typography>
                      <Typography variant="h4" color="success.main">
                        ₦{stats.totalCollected.toLocaleString()}
                      </Typography>
                    </Box>
                    <Download sx={{ fontSize: 40, color: 'success.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Total Bookings
                      </Typography>
                      <Typography variant="h4" color="info.main">
                        {stats.totalBookings}
                      </Typography>
                    </Box>
                    <RoomService sx={{ fontSize: 40, color: 'info.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Pending Payments
                      </Typography>
                      <Typography variant="h4" color="warning.main">
                        {stats.pendingPayments}
                      </Typography>
                    </Box>
                    <Receipt sx={{ fontSize: 40, color: 'warning.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Active Filters */}
          {(filters.facilityId || filters.bookingStatus || filters.paymentStatus || filters.createdBy) && (
            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Active Filters:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {filters.facilityId && (
                  <Chip 
                    label={`Facility: ${facilities.find(f => f.id == filters.facilityId)?.name || filters.facilityId}`} 
                    size="small" 
                    onDelete={() => handleFilterChange('facilityId')({ target: { value: '' } })}
                  />
                )}
                {filters.bookingStatus && (
                  <Chip 
                    label={`Status: ${filters.bookingStatus}`} 
                    size="small" 
                    onDelete={() => handleFilterChange('bookingStatus')({ target: { value: '' } })}
                  />
                )}
                {filters.paymentStatus && (
                  <Chip 
                    label={`Payment: ${filters.paymentStatus}`} 
                    size="small" 
                    onDelete={() => handleFilterChange('paymentStatus')({ target: { value: '' } })}
                  />
                )}
                {filters.createdBy && (
                  <Chip 
                    label={`Staff: ${staffList.find(s => s.id == filters.createdBy)?.first_name || filters.createdBy}`} 
                    size="small" 
                    onDelete={() => handleFilterChange('createdBy')({ target: { value: '' } })}
                  />
                )}
                <Button size="small" onClick={clearFilters}>
                  Clear All
                </Button>
              </Stack>
            </Paper>
          )}

          {/* Bookings Table */}
          <Paper elevation={3}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: theme.palette.primary.main }}>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Booking #</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Facility Booked</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date & Time</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Amount</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Payment</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Staff</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredBookings
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((booking) => (
                        <TableRow key={booking.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {booking.booking_number}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {booking.facility_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {booking.guest_name || 'Walk-in Guest'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{booking.booking_date}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {booking.booking_time}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold" color="primary">
                              ₦{parseFloat(booking.total_amount).toLocaleString()}
                            </Typography>
                            {booking.discount_percentage > 0 && (
                              <Typography variant="caption" color="secondary">
                                {booking.discount_percentage}% OFF
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={booking.booking_status} 
                              color={getStatusColor(booking.booking_status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Chip 
                                label={booking.payment_status} 
                                color={getStatusColor(booking.payment_status)}
                                size="small"
                              />
                              {booking.payment_method && (
                                <Typography variant="caption">
                                  {booking.payment_method}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {booking.created_by_name}
                            </Typography>
                            {booking.checked_in_by_name && (
                              <Typography variant="caption" color="textSecondary">
                                Check-in: {booking.checked_in_by_name}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="View Details">
                                <IconButton 
                                  size="small" 
                                  color="primary"
                                  onClick={() => handleViewDetails(booking)}
                                >
                                  <Visibility />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Print Receipt">
                                <IconButton 
                                  size="small" 
                                  color="info"
                                  onClick={() => prepareReceiptFromBooking(booking)}
                                >
                                  <Print />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {filteredBookings.length === 0 && (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="textSecondary">
                      No facility bookings found
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      {Object.values(filters).some(f => f) 
                        ? 'Try adjusting your filters or date range' 
                        : 'No facility bookings have been made yet'
                      }
                    </Typography>
                  </Box>
                )}

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredBookings.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
              </>
            )}
          </Paper>
        </Box>
      </Box>

      {/* Filter Dialog */}
      <Dialog 
        open={filterDialogOpen} 
        onClose={() => setFilterDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList color="primary" />
            <Typography variant="h6">Filter Bookings</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={filters.startDate}
                onChange={handleDateChange('startDate')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={filters.endDate}
                onChange={handleDateChange('endDate')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Facility</InputLabel>
                <Select
                  value={filters.facilityId}
                  onChange={handleFilterChange('facilityId')}
                  label="Facility"
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="">All Facilities</MenuItem>
                  {facilities.map((facility) => (
                    <MenuItem key={facility.id} value={facility.id}>
                      {facility.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Booking Status</InputLabel>
                <Select
                  value={filters.bookingStatus}
                  onChange={handleFilterChange('bookingStatus')}
                  label="Booking Status"
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="checked_in">Checked In</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Status</InputLabel>
                <Select
                  value={filters.paymentStatus}
                  onChange={handleFilterChange('paymentStatus')}
                  label="Payment Status"
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="partial">Partial</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Staff Member</InputLabel>
                <Select
                  value={filters.createdBy}
                  onChange={handleFilterChange('createdBy')}
                  label="Staff Member"
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="">All Staff</MenuItem>
                  {staffList.map((staff) => (
                    <MenuItem key={staff.id} value={staff.id}>
                      {staff.first_name} {staff.other_names}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearFilters}>Clear All</Button>
          <Button onClick={() => setFilterDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={() => setFilterDialogOpen(false)}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Booking Dialog */}
      <Dialog 
        open={bookingDialogOpen} 
        onClose={() => setBookingDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">New Facility Booking</Typography>
          <Stepper activeStep={activeStep} sx={{ mt: 2 }}>
            {bookingSteps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </DialogTitle>
        <DialogContent dividers>
          {activeStep === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth required error={!!formErrors.facility_id}>
                  <InputLabel>Select Facility *</InputLabel>
                  <Select
                    value={bookingForm.facility_id}
                    onChange={handleBookingInputChange('facility_id')}
                    label="Select Facility *"
                    sx={{ minWidth: 250 }}
                  >
                    <MenuItem value="">
                      <em>Choose a facility</em>
                    </MenuItem>
                    {facilities
                      .filter(f => f.requires_booking)
                      .map(facility => {
                        const fee = facility.booking_fee || 0;
                        const { discountPercentage } = getFacilityWithDiscount(facility);
                        return (
                          <MenuItem key={facility.id} value={facility.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between', width: '100%' }}>
                              <span>{facility.name}</span>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <span>₦{fee.toLocaleString()}</span>
                                {discountPercentage > 0 && (
                                  <Chip 
                                    label={`${discountPercentage}% OFF`} 
                                    size="small" 
                                    color="secondary"
                                    icon={<Discount />}
                                  />
                                )}
                              </Box>
                            </Box>
                          </MenuItem>
                        );
                      })}
                  </Select>
                  {formErrors.facility_id && (
                    <Typography variant="caption" color="error">{formErrors.facility_id}</Typography>
                  )}
                </FormControl>
              </Grid>
              {bookingForm.facility_id && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    <Typography variant="body2" gutterBottom>
                      <strong>Selected Facility:</strong> {facilities.find(f => f.id == bookingForm.facility_id)?.name}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Base Price:</strong> ₦{facilities.find(f => f.id == bookingForm.facility_id)?.booking_fee?.toLocaleString()}
                    </Typography>
                    {getCurrentDiscountPercentage() > 0 && (
                      <Typography variant="body2" color="secondary">
                        <strong>Special Discount:</strong> {getCurrentDiscountPercentage()}% OFF
                      </Typography>
                    )}
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}

          {activeStep === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Search Customer (Email or Phone)"
                  onChange={(e) => handleSearchGuests(e.target.value)}
                  placeholder="Start typing to search for existing guests..."
                />
              </Grid>
              {guests.length > 0 && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Select Guest</InputLabel>
                    <Select
                      value={bookingForm.guest_id}
                      onChange={(e) => {
                        const guest = guests.find(g => g.id == e.target.value);
                        if (guest) handleSelectGuest(guest);
                      }}
                      label="Select Guest"
                      sx={{ minWidth: 200 }}
                    >
                      <MenuItem value="">New Walk-in Guest</MenuItem>
                      {guests.map((guest) => (
                        <MenuItem key={guest.id} value={guest.id}>
                          {guest.first_name} {guest.last_name} - {guest.email} - {guest.phone}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12}>
                <Divider>Guest Information *</Divider>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Guest Full Name *"
                  value={bookingForm.guest_name}
                  onChange={handleBookingInputChange('guest_name')}
                  error={!!formErrors.guest_name}
                  helperText={formErrors.guest_name}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Guest Phone"
                  value={bookingForm.guest_phone}
                  onChange={handleBookingInputChange('guest_phone')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Guest Email"
                  type="email"
                  value={bookingForm.guest_email}
                  onChange={handleBookingInputChange('guest_email')}
                />
              </Grid>
            </Grid>
          )}

          {activeStep === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Booking Date *"
                  type="date"
                  value={bookingForm.booking_date}
                  onChange={handleBookingInputChange('booking_date')}
                  InputLabelProps={{ shrink: true }}
                  error={!!formErrors.booking_date}
                  helperText={formErrors.booking_date}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required error={!!formErrors.booking_time}>
                  <InputLabel>Booking Time *</InputLabel>
                  <Select
                    value={bookingForm.booking_time}
                    onChange={handleBookingInputChange('booking_time')}
                    label="Booking Time *"
                    sx={{ minWidth: 150 }}
                  >
                    {timeSlots.map((time) => (
                      <MenuItem key={time} value={time}>
                        {time}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.booking_time && (
                    <Typography variant="caption" color="error">{formErrors.booking_time}</Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Duration (minutes)"
                  type="number"
                  value={bookingForm.duration}
                  onChange={handleBookingInputChange('duration')}
                  InputProps={{ endAdornment: <Typography>mins</Typography> }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Number of Guests"
                  type="number"
                  value={bookingForm.number_of_guests}
                  onChange={handleBookingInputChange('number_of_guests')}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Special Requests"
                  multiline
                  rows={3}
                  value={bookingForm.special_requests}
                  onChange={handleBookingInputChange('special_requests')}
                />
              </Grid>
            </Grid>
          )}

          {activeStep === 3 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required error={!!formErrors.payment_method}>
                  <InputLabel>Payment Method *</InputLabel>
                  <Select
                    value={bookingForm.payment_method}
                    onChange={handleBookingInputChange('payment_method')}
                    label="Payment Method *"
                    sx={{ minWidth: 200 }}
                  >
                    <MenuItem value="Cash">Cash</MenuItem>
                    <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                    <MenuItem value="POS Terminal">POS Terminal</MenuItem>
                    <MenuItem value="paystack">Paystack</MenuItem>
                  </Select>
                  {formErrors.payment_method && (
                    <Typography variant="caption" color="error">{formErrors.payment_method}</Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2" gutterBottom>
                    <strong>Price Breakdown:</strong>
                  </Typography>
                  <Typography variant="body2">
                    Base Price: ₦{(() => {
                      const facility = facilities.find(f => f.id == bookingForm.facility_id);
                      return (facility?.booking_fee || 0).toLocaleString();
                    })()}
                  </Typography>
                  {bookingForm.number_of_guests > 1 && (
                    <Typography variant="body2">
                      Extra Guests ({(bookingForm.number_of_guests - 1)} × ₦1,000): ₦{((bookingForm.number_of_guests - 1) * 1000).toLocaleString()}
                    </Typography>
                  )}
                  {getCurrentDiscountPercentage() > 0 && (
                    <>
                      <Typography variant="body2" color="secondary">
                        Discount ({getCurrentDiscountPercentage()}% OFF): -₦{Math.round(calculateDiscountAmount()).toLocaleString()}
                      </Typography>
                      {(() => {
                        const facility = facilities.find(f => f.id == bookingForm.facility_id);
                        const { discountReason } = getFacilityWithDiscount(facility);
                        return discountReason && (
                          <Typography variant="caption" color="secondary">
                            Reason: {discountReason}
                          </Typography>
                        );
                      })()}
                    </>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                    Total Amount: ₦{calculateTotalAmount().toLocaleString()}
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBookingDialogOpen(false)}>Cancel</Button>
          <Box sx={{ flex: 1 }} />
          {activeStep > 0 && (
            <Button onClick={handleBackStep}>Back</Button>
          )}
          {activeStep < bookingSteps.length - 1 ? (
            <Button 
              variant="contained" 
              onClick={handleNextStep}
            >
              Next
            </Button>
          ) : (
            <Button 
              variant="contained" 
              onClick={handleSubmitBooking}
            >
              Complete Booking & Print Receipt
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Receipt Print Dialog */}
      <Dialog 
        open={printDialogOpen} 
        onClose={() => setPrintDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Print Receipt</Typography>
        </DialogTitle>
        <DialogContent>
          <Box id="receipt-content" sx={{ p: 2, fontFamily: 'monospace' }}>
            {/* Receipt Header */}
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="h6" gutterBottom>
               {HOTEL_NAME}
              </Typography>
              <Typography variant="caption" display="block">
                 {HOTEL_ADDRESS}
              </Typography>
              <Typography variant="caption" display="block">
              Tel: {HOTEL_PHONE}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" fontWeight="bold">
                FACILITY BOOKING RECEIPT
              </Typography>
              <Divider sx={{ my: 1 }} />
            </Box>

            {/* Receipt Details */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Receipt No:</strong> {receiptData?.booking?.booking_number || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Date:</strong> {receiptData?.date ? new Date(receiptData.date).toLocaleString() : new Date().toLocaleString()}
              </Typography>
              <Typography variant="body2">
                <strong>Facility:</strong> {receiptData?.facility?.name || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Booking Date:</strong> {receiptData?.booking_date || 'N/A'} at {receiptData?.booking_time || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Duration:</strong> {receiptData?.duration || 60} minutes
              </Typography>
              <Typography variant="body2">
                <strong>Number of Guests:</strong> {receiptData?.number_of_guests || 1}
              </Typography>
            </Box>

            <Divider />

            {/* Customer Information */}
            <Box sx={{ my: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold">Customer Details:</Typography>
              <Typography variant="body2">
                <strong>Name:</strong> {receiptData?.guest?.guest_name || receiptData?.guest_name || 'Walk-in Guest'}
              </Typography>
              {receiptData?.guest?.guest_phone && (
                <Typography variant="body2">
                  <strong>Phone:</strong> {receiptData.guest.guest_phone}
                </Typography>
              )}
              {receiptData?.guest?.guest_email && (
                <Typography variant="body2">
                  <strong>Email:</strong> {receiptData.guest.guest_email}
                </Typography>
              )}
            </Box>

            <Divider />

            {/* Payment Breakdown */}
            <Box sx={{ my: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold">Payment Breakdown:</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="body2">Base Price:</Typography>
                <Typography variant="body2">₦{receiptData?.baseAmount?.toLocaleString() || (receiptData?.facility?.booking_fee || 0).toLocaleString()}</Typography>
              </Box>
              {receiptData?.number_of_guests > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Extra Guests Fee:</Typography>
                  <Typography variant="body2">₦{((receiptData.number_of_guests - 1) * 1000).toLocaleString()}</Typography>
                </Box>
              )}
              {receiptData?.discountPercentage > 0 && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', color: 'secondary.main' }}>
                    <Typography variant="body2">Discount ({receiptData.discountPercentage}%):</Typography>
                    <Typography variant="body2">-₦{Math.round(receiptData.discountAmount).toLocaleString()}</Typography>
                  </Box>
                  <Typography variant="caption" display="block" color="secondary">
                    Reason: {receiptData.discountPercentage > 0 ? 'Facility Special Discount' : ''}
                  </Typography>
                </>
              )}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                <Typography variant="body1" fontWeight="bold">Total Paid:</Typography>
                <Typography variant="body1" fontWeight="bold" color="primary">
                  ₦{receiptData?.totalAmount?.toLocaleString() || 0}
                </Typography>
              </Box>
              <Typography variant="body2">
                <strong>Payment Method:</strong> {receiptData?.payment_method || 'Cash'}
              </Typography>
            </Box>

            <Divider />

            {/* Staff Information */}
            <Box sx={{ my: 2 }}>
              <Typography variant="body2">
                <strong>Processed by:</strong> {receiptData?.staff?.first_name} {receiptData?.staff?.last_name || ''}
              </Typography>
              <Typography variant="body2">
                <strong>Staff ID:</strong> {receiptData?.staff?.staff_id || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Position:</strong> {receiptData?.staff?.user_type || 'Staff'}
              </Typography>
            </Box>

            <Divider />

            {/* Signatures */}
            <Box sx={{ my: 2, display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption">Customer Signature</Typography>
                <Box sx={{ mt: 4, width: 150, borderBottom: '1px solid black' }} />
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption">Hotel Stamp/Signature</Typography>
                <Box sx={{ mt: 4, width: 150, borderBottom: '1px solid black' }} />
              </Box>
            </Box>

            {/* Footer */}
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="caption" display="block">
                Thank you for choosing our facility!
              </Typography>
             
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintDialogOpen(false)}>Close</Button>
          <Button 
            variant="contained" 
            startIcon={<Print />}
            onClick={handlePrintReceipt}
          >
            Print Receipt
          </Button>
        </DialogActions>
      </Dialog>

      {/* Booking Details Dialog */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Booking Details</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedBooking && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Booking Information</Typography>
                <Typography variant="body1" gutterBottom><strong>Booking #:</strong> {selectedBooking.booking_number}</Typography>
                <Typography variant="body1" gutterBottom><strong>Facility:</strong> {selectedBooking.facility_name}</Typography>
                <Typography variant="body1" gutterBottom><strong>Category:</strong> {selectedBooking.facility_category}</Typography>
                <Typography variant="body1" gutterBottom><strong>Date:</strong> {selectedBooking.booking_date}</Typography>
                <Typography variant="body1" gutterBottom><strong>Time:</strong> {selectedBooking.booking_time}</Typography>
                <Typography variant="body1" gutterBottom><strong>Duration:</strong> {selectedBooking.duration} minutes</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Guest Information</Typography>
                <Typography variant="body1" gutterBottom><strong>Name:</strong> {selectedBooking.guest_name || 'Walk-in Guest'}</Typography>
                {selectedBooking.guest_email && (
                  <Typography variant="body1" gutterBottom><strong>Email:</strong> {selectedBooking.guest_email}</Typography>
                )}
                {selectedBooking.guest_phone && (
                  <Typography variant="body1" gutterBottom><strong>Phone:</strong> {selectedBooking.guest_phone}</Typography>
                )}
                <Typography variant="body1" gutterBottom><strong>Number of Guests:</strong> {selectedBooking.number_of_guests}</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Financial Details</Typography>
                <Typography variant="body1" gutterBottom><strong>Total Amount:</strong> ₦{parseFloat(selectedBooking.total_amount).toLocaleString()}</Typography>
                <Typography variant="body1" gutterBottom><strong>Paid Amount:</strong> ₦{parseFloat(selectedBooking.paid_amount || 0).toLocaleString()}</Typography>
                <Typography variant="body1" gutterBottom><strong>Payment Method:</strong> {selectedBooking.payment_method || 'Not specified'}</Typography>
                {selectedBooking.discount_percentage > 0 && (
                  <Typography variant="body1" gutterBottom><strong>Discount:</strong> {selectedBooking.discount_percentage}% OFF</Typography>
                )}
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Status Information</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Chip 
                    label={selectedBooking.booking_status} 
                    color={getStatusColor(selectedBooking.booking_status)}
                  />
                  <Chip 
                    label={selectedBooking.payment_status} 
                    color={getStatusColor(selectedBooking.payment_status)}
                  />
                </Box>
                <Typography variant="body1" gutterBottom><strong>Created by:</strong> {selectedBooking.created_by_name}</Typography>
                {selectedBooking.checked_in_by_name && (
                  <Typography variant="body1" gutterBottom><strong>Checked in by:</strong> {selectedBooking.checked_in_by_name}</Typography>
                )}
                {selectedBooking.special_requests && (
                  <Typography variant="body1" gutterBottom><strong>Special Requests:</strong> {selectedBooking.special_requests}</Typography>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          <Button 
            variant="contained" 
            startIcon={<Print />}
            onClick={() => {
              setDetailsDialogOpen(false);
              prepareReceiptFromBooking(selectedBooking);
            }}
          >
            Print Receipt
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FacilitySalesManagement;